import React, { useEffect, useMemo, useState } from 'react';
import parse from 'html-react-parser';

const PLACEHOLDER_REGEX =
  /<div[^>]*data-image-placeholder="(\d+)"[^>]*>\s*<\/div>/gi;

const splitContentByPlaceholders = (html = '') => {
  const matches = [...html.matchAll(PLACEHOLDER_REGEX)];

  if (!matches.length) {
    return [{ type: 'content', html }];
  }

  const sections = [];
  const firstMatchIndex = matches[0].index ?? 0;

  const introHtml = html.slice(0, firstMatchIndex);
  if (introHtml.trim()) {
    sections.push({
      type: 'content',
      html: introHtml,
    });
  }

  matches.forEach((match, idx) => {
    const start = (match.index ?? 0) + match[0].length;
    const end =
      idx + 1 < matches.length
        ? matches[idx + 1].index ?? html.length
        : html.length;

    const chunkHtml = html.slice(start, end);
    const placeholderNumber = Number(match[1]);

    if (chunkHtml.trim()) {
      sections.push({
        type: 'image-section',
        html: chunkHtml,
        placeholderNumber,
        sectionIndex: idx,
      });
    }
  });

  return sections;
};

const SectionImage = ({ src, alt, onOrientation }) => {
  useEffect(() => {
    if (!src) return;

    const probe = new Image();
    probe.src = src;

    probe.onload = () => {
      const isPortrait = probe.naturalHeight > probe.naturalWidth;
      onOrientation?.(isPortrait);
    };

    probe.onerror = () => {
      onOrientation?.(false);
    };
  }, [src, onOrientation]);

  return (
    <img
      src={src}
      alt={alt}
      className="inline-article-image"
      loading="lazy"
    />
  );
};

const ArticleImageSection = ({ html, imageUrl, alt }) => {
  const [isPortrait, setIsPortrait] = useState(true);

  const layoutClass = imageUrl
    ? isPortrait
      ? 'has-side-image'
      : 'has-block-image'
    : 'no-image';

  const media = imageUrl ? (
    <aside className="article-section-media">
      <SectionImage
        src={imageUrl}
        alt={alt}
        onOrientation={setIsPortrait}
      />
    </aside>
  ) : null;

  return (
    <section className={`article-section-with-image ${layoutClass}`}>
      {layoutClass === 'has-block-image' && media}

      <div className="article-section-main">
        {parse(html || '')}
      </div>

      {layoutClass === 'has-side-image' && media}
    </section>
  );
};

const ContentRenderer = ({ htmlContent, images = [] }) => {
  const sections = useMemo(
    () => splitContentByPlaceholders(htmlContent || ''),
    [htmlContent]
  );

  return (
    <>
      {sections.map((section, index) => {
        if (section.type === 'content') {
          return (
            <div key={`content-${index}`} className="article-content-chunk">
              {parse(section.html || '')}
            </div>
          );
        }

        const imageIndex =
          Number.isFinite(section.placeholderNumber) &&
          section.placeholderNumber > 0
            ? section.placeholderNumber - 1
            : section.sectionIndex;

        const imageUrl = images?.[imageIndex] || null;

        return (
          <ArticleImageSection
            key={`image-section-${index}`}
            html={section.html}
            imageUrl={imageUrl}
            alt={`Blog content ${imageIndex + 1}`}
          />
        );
      })}
    </>
  );
};

export default ContentRenderer;
